<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="filteredItems"
            item-key="rowKey"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            :items-per-page="100"
            :footer-props="{
                itemsPerPageOptions: [50, 100, 250, 500],
                itemsPerPageText: 'Rows per page',
            }"
            :height="$vuetify.breakpoint.mdAndDown ? '61vh' : '55vh'"
            fixed-header
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-2 px-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex align-center flex-wrap">
                        <h1 class="mr-4 font-weight-bold">PL ARCHIVE</h1>
                        <v-chip
                            v-if="loadingMore"
                            small
                            outlined
                            color="primary"
                            class="mr-2"
                        >
                            Loading remaining packing lists ({{
                                loadProgress.loaded
                            }}
                            / {{ loadProgress.total }})
                        </v-chip>
                    </v-col>
                </v-row>
            </template>

            <template v-slot:[`header.workOrder`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.workOrder"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.project"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.packageLabel`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.packageLabel"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.itemCode`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.itemCode"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.description`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.description"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.finishAndUCCode`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.finishAndUCCode"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.notes`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.notes"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.container`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.container"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.shippedOn`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.shippedOn"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.packingQty`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.packingQty"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.qtyWarehouseReceipt`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.qtyWarehouseReceipt"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.qtySentToSite`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.qtySentToSite"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.qtySiteReceipt`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="filters.qtySiteReceipt"
                    dense
                    class="pt-2"
                />
            </template>

            <template v-slot:[`item.itemCode`]="{ item }">
                <a
                    v-if="item.workOrder && item.itemCode"
                    class="primary--text"
                    style="cursor: pointer"
                    @click.stop="openPartNumberPdf(item)"
                >
                    {{ item.itemCode }}
                </a>
                <span v-else>{{ item.itemCode }}</span>
            </template>

            <template v-slot:[`item.qtySentToSite`]="{ item }">
                <a
                    v-if="Number(item.qtySentToSite) > 0"
                    class="primary--text"
                    @click.stop="openSentToSiteTickets(item)"
                >
                    {{ item.qtySentToSite }}
                </a>
                <span v-else>{{ item.qtySentToSite ?? '' }}</span>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
                <div
                    v-if="hasAnyStockESMPermission"
                    class="d-flex justify-center"
                >
                    <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn small icon v-bind="attrs" v-on="on">
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item
                                :disabled="
                                    !canRecordWarehouseReceipt ||
                                        isWarehouseReceiptComplete(item)
                                "
                                @click="openWarehouseReceipt(item)"
                            >
                                <v-list-item-title>
                                    Record warehouse receipt
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                :disabled="
                                    !canSendToSite ||
                                        (Number(item.qtyWarehouseReceipt) ||
                                            0) -
                                            (Number(item.qtySentToSite) || 0) <=
                                            0
                                "
                                @click="openSentToSite(item)"
                            >
                                <v-list-item-title>
                                    Mark as sent to site
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                :disabled="
                                    !canRecordSiteReceipt ||
                                        (Number(item.qtySentToSite) || 0) -
                                            (Number(item.qtySiteReceipt) ||
                                                0) <=
                                            0
                                "
                                @click="openSiteReceipt(item)"
                            >
                                <v-list-item-title>
                                    Record site receipt
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </template>
        </v-data-table>

        <v-dialog
            v-model="openTicketsDialog"
            :retain-focus="false"
            persistent
            :width="$vuetify.breakpoint.mobile ? '95%' : '75%'"
        >
            <SentToSiteTicketsDialog
                v-if="openTicketsDialog"
                :item="selectedItem"
                :items="items"
                :company-id="companyId"
                :can-upload-signed="canSendToSite"
                :projects="projects"
                :loading-projects="loadingProjects"
                @closeDialog="closeTicketsDialog"
                @signedUploaded="onSignedTicketUploaded"
            />
        </v-dialog>

        <v-dialog
            v-model="openSentToSiteDialog"
            :retain-focus="false"
            persistent
            :width="$vuetify.breakpoint.mobile ? '95%' : '80%'"
        >
            <MarkSentToSite
                v-if="openSentToSiteDialog"
                :item="selectedItem"
                :available-items="sentToSiteAvailableItems"
                :company-id="companyId"
                :projects="projects"
                :loading-projects="loadingProjects"
                @closeDialog="closeSentToSiteDialog"
                @saved="onSentToSiteSaved"
            />
        </v-dialog>

        <v-dialog
            v-model="openWarehouseDialog"
            :retain-focus="false"
            persistent
            :width="$vuetify.breakpoint.mobile ? '95%' : '75%'"
        >
            <RecordWarehouseReceipt
                v-if="openWarehouseDialog"
                :item="selectedItem"
                @closeDialog="closeWarehouseDialog"
                @saved="onReceiptSaved"
            />
        </v-dialog>

        <v-dialog
            v-model="openSiteDialog"
            :retain-focus="false"
            persistent
            :width="$vuetify.breakpoint.mobile ? '95%' : '75%'"
        >
            <RecordSiteReceipt
                v-if="openSiteDialog"
                :item="selectedItem"
                @closeDialog="closeSiteDialog"
                @saved="onReceiptSaved"
            />
        </v-dialog>

        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
    </div>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'
import moment from 'moment'
import { loadImage } from '@/helpers/imageHandler.js'
import RecordWarehouseReceipt from '@/components/StockRoom/RecordWarehouseReceipt.vue'
import RecordSiteReceipt from '@/components/StockRoom/RecordSiteReceipt.vue'
import MarkSentToSite from '@/components/StockRoom/MarkSentToSite.vue'
import SentToSiteTicketsDialog from '@/components/StockRoom/SentToSiteTicketsDialog.vue'
import {
    ESM_SOURCE_COMPANY_ID,
    isStockESMViewerCompany,
} from '@/utils/stockESMConfig'

export default {
    name: 'StockESM',
    components: {
        RecordWarehouseReceipt,
        RecordSiteReceipt,
        MarkSentToSite,
        SentToSiteTicketsDialog,
    },
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
    },
    data: () => ({
        loading: false,
        loadingMore: false,
        loadRequestId: 0,
        loadProgress: {
            loaded: 0,
            total: 0,
        },
        items: [],
        workOrderIdByCode: {},
        settingFinish: null,
        companyId: JSON.parse(localStorage.getItem('company')),
        sourceCompanyId: ESM_SOURCE_COMPANY_ID,
        openWarehouseDialog: false,
        openSiteDialog: false,
        openSentToSiteDialog: false,
        openTicketsDialog: false,
        sentToSiteAvailableItems: [],
        selectedItem: {},
        activateAlert: false,
        alertMessage: '',
        canRecordWarehouseReceipt: false,
        canSendToSite: false,
        canRecordSiteReceipt: false,
        projects: [],
        loadingProjects: false,
        filters: {
            workOrder: undefined,
            project: undefined,
            packageLabel: undefined,
            itemCode: undefined,
            description: undefined,
            finishAndUCCode: undefined,
            notes: undefined,
            container: undefined,
            shippedOn: undefined,
            packingQty: undefined,
            qtyWarehouseReceipt: undefined,
            qtySentToSite: undefined,
            qtySiteReceipt: undefined,
        },
        headers: [
            {
                text: 'WORK ORDER',
                value: 'workOrder',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'left',
                sortable: false,
            },
            // {
            //     text: 'PACKING LIST',
            //     value: 'packingListCode',
            //     align: 'center',
            //     sortable: false,
            // },
            {
                text: 'PACKAGE',
                value: 'packageLabel',
                align: 'center',
                sortable: false,
            },
            {
                text: 'ITEM CODE',
                value: 'itemCode',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'FINISH & UC CODE',
                value: 'finishAndUCCode',
                align: 'center',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'left',
                sortable: false,
            },
            {
                text: 'CONTAINER',
                value: 'container',
                align: 'center',
                sortable: false,
            },
            {
                text: 'SHIPPED ON',
                value: 'shippedOn',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PACKING QTY',
                value: 'packingQty',
                align: 'center',
                sortable: false,
            },
            {
                text: 'WAREHOUSE RECEIVED QTY',
                value: 'qtyWarehouseReceipt',
                align: 'center',
                sortable: false,
            },
            {
                text: 'SENT TO SITE QTY',
                value: 'qtySentToSite',
                align: 'center',
                sortable: false,
            },
            {
                text: 'SITE RECEIVED QTY',
                value: 'qtySiteReceipt',
                align: 'center',
                sortable: false,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
    }),
    computed: {
        hasAnyStockESMPermission() {
            return (
                this.canRecordWarehouseReceipt ||
                this.canSendToSite ||
                this.canRecordSiteReceipt
            )
        },
        isProjectStockESMView() {
            const routeName = this.$route.name || ''
            return [
                'project-stock-esm',
                'project-stock-esm-generic',
                'archive-project-stock-esm',
            ].includes(routeName)
        },
        resolvedViewerProjectId() {
            return (
                this.projectId ||
                this.$route.params.projectId ||
                this.$route.params.id ||
                null
            )
        },
        esmViewerProjectId() {
            if (!this.isProjectStockESMView || !this.resolvedViewerProjectId) {
                return undefined
            }
            return this.resolvedViewerProjectId
        },
        filteredItems() {
            const activeFilterKeys = Object.keys(this.filters).filter(
                key => this.filters[key]
            )

            if (!activeFilterKeys.length) {
                return this.items
            }

            return this.items.filter(item =>
                activeFilterKeys.every(key =>
                    this.filterByField(item[key], this.filters[key])
                )
            )
        },
    },
    watch: {
        esmViewerProjectId() {
            this.loadItems()
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        filterByField(value, textToFilter) {
            return String(value ?? '')
                .toLowerCase()
                .includes(textToFilter.toLowerCase())
        },
        loadStockESMPermissions(user = {}) {
            const userPermissions = user?.permissions || []
            this.canRecordWarehouseReceipt = userPermissions.includes(
                'recordESMWarehouseReceipt'
            )
            this.canSendToSite = userPermissions.includes('sendESMToSite')
            this.canRecordSiteReceipt = userPermissions.includes(
                'recordESMSiteReceipt'
            )
        },
        getProjectStockFallbackRoute() {
            const path = this.$route.path || ''
            if (path.includes('archive-project')) {
                return 'archive-project-stock-items'
            }
            if (path.includes('projects-generic')) {
                return 'project-stock-items-generic'
            }
            if (path.includes('/projects/')) {
                return 'project-stock-items'
            }
            return 'stock'
        },
        async loadSettings() {
            try {
                const {
                    data: { settings },
                } = await API.getSettings()
                if (Array.isArray(settings)) {
                    this.settingFinish =
                        settings.find(s => s.name == 'FinishAndUCCode') || null
                }
            } catch (settingsError) {
                this.settingFinish = null
            }
        },
        async loadProjects() {
            try {
                this.loadingProjects = true
                const projects = await API.getLiteProjects({ all: true })
                this.projects = (projects || []).map(project => ({
                    ...project,
                    label: `${project.reference || ''}${
                        project.name ? ` - ${project.name}` : ''
                    }`.trim(),
                }))
            } catch (error) {
                this.projects = []
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingProjects = false
            }
        },
        async ensureProjectsLoaded() {
            if (this.projects.length || this.loadingProjects) {
                return
            }
            await this.loadProjects()
        },
        async loadItems() {
            const requestId = ++this.loadRequestId
            const PAGE_SIZE = 15
            try {
                this.loading = true
                this.loadingMore = false
                this.loadProgress = { loaded: 0, total: 0 }

                const first = await API.getESMStockPackingLists({
                    viewerProjectId: this.esmViewerProjectId,
                    limit: PAGE_SIZE,
                })
                if (requestId !== this.loadRequestId) {
                    return
                }

                this.items = this.mapStockRows(first?.items)
                this.loading = false

                const allIds = first?.packingListIds || []
                const loadedPackingListIds = new Set(
                    this.items.map(row => row.packingListId)
                )
                this.loadProgress = {
                    loaded: loadedPackingListIds.size,
                    total: first?.totalPackingLists || allIds.length,
                }

                const remainingIds = allIds.filter(
                    id => !loadedPackingListIds.has(id)
                )
                if (!remainingIds.length) {
                    return
                }

                this.loadingMore = true
                for (let i = 0; i < remainingIds.length; i += PAGE_SIZE) {
                    if (requestId !== this.loadRequestId) {
                        return
                    }
                    const chunkIds = remainingIds.slice(i, i + PAGE_SIZE)
                    try {
                        const page = await API.getESMStockPackingLists({
                            packingListIds: chunkIds,
                        })
                        if (requestId !== this.loadRequestId) {
                            return
                        }
                        this.items = this.items.concat(
                            this.mapStockRows(page?.items)
                        )
                        this.loadProgress = {
                            loaded: new Set(
                                this.items.map(row => row.packingListId)
                            ).size,
                            total: allIds.length,
                        }
                    } catch (pageError) {
                        this.setErrorItems({
                            source: this.$options.name,
                            message: `${pageError.message}. Showing packing lists loaded so far.`,
                        })
                        break
                    }
                }
            } catch (error) {
                if (requestId !== this.loadRequestId) {
                    return
                }
                if (this.items.length > 0) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: `${error.message}. Showing last loaded data.`,
                    })
                    return
                }
                this.items = []
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                if (requestId === this.loadRequestId) {
                    this.loading = false
                    this.loadingMore = false
                }
            }
        },
        mapStockRows(items) {
            return (items || []).map(row => ({
                ...row,
                rowKey: this.getRowKey(row),
            }))
        },
        waitForFullLoad() {
            if (!this.loading && !this.loadingMore) {
                return Promise.resolve()
            }
            return new Promise(resolve => {
                const unwatch = this.$watch(
                    () => this.loading || this.loadingMore,
                    busy => {
                        if (!busy) {
                            unwatch()
                            resolve()
                        }
                    }
                )
                if (!this.loading && !this.loadingMore) {
                    unwatch()
                    resolve()
                }
            })
        },
        async applyPackingListUpdates(packingLists) {
            if (!this.settingFinish) {
                await this.loadSettings()
            }
            const lists = []
                .concat(packingLists)
                .filter(packingList => packingList?.id)
            if (!lists.length) {
                return
            }

            lists.forEach(packingList => {
                const sampleRow = this.items.find(
                    row => row.packingListId === packingList.id
                )
                const enrichedList = {
                    ...packingList,
                    code: packingList.code || sampleRow?.packingListCode || '',
                    container:
                        packingList.container || sampleRow?.container || '',
                    projectReference: packingList.projectReference || '',
                    projectName: packingList.projectName || '',
                }

                if (sampleRow && !enrichedList.projectReference) {
                    const projectParts = (sampleRow.project || '').split(' - ')
                    enrichedList.projectReference = projectParts[0] || ''
                    enrichedList.projectName =
                        projectParts.slice(1).join(' - ') || ''
                }

                const packages = (enrichedList.packages || []).map(pkg => ({
                    ...pkg,
                    items: (pkg.items || []).map((item, itemIndex) => {
                        const existingRow = this.items.find(
                            row =>
                                row.packingListId === packingList.id &&
                                row.packageId === pkg.id &&
                                row.itemIndex === itemIndex
                        )
                        return {
                            ...item,
                            workOrderId:
                                item.workOrderId ||
                                existingRow?.workOrderId ||
                                null,
                            finishAndUCCodeLabel:
                                item.finishAndUCCodeLabel ||
                                existingRow?.finishAndUCCode ||
                                '',
                        }
                    }),
                }))

                const newRows = this.flattenPackingLists([
                    { ...enrichedList, packages },
                ])

                const firstIndex = this.items.findIndex(
                    row => row.packingListId === packingList.id
                )
                if (firstIndex === -1) {
                    this.items = [...this.items, ...newRows]
                    return
                }

                const nextItems = this.items.filter(
                    row => row.packingListId !== packingList.id
                )
                nextItems.splice(firstIndex, 0, ...newRows)
                this.items = nextItems
            })
        },
        flattenPackingLists(packingLists) {
            const rows = []
            packingLists.forEach(packingList => {
                const projectLabel = `${packingList.projectReference || ''}${
                    packingList.projectName
                        ? ` - ${packingList.projectName}`
                        : ''
                }`.trim()
                const shippedOn = this.formatTimestamp(packingList.shippedOn)

                ;(packingList.packages || []).forEach((pkg, packageIndex) => {
                    ;(pkg.items || []).forEach((item, itemIndex) => {
                        const row = {
                            workOrder: item.workOrder || item.bom || '',
                            project: projectLabel,
                            packingListCode: packingList.code || '',
                            packageLabel: `Package ${packageIndex + 1}`,
                            itemCode: item.partNumber || item.code || '',
                            description: item.description || '',
                            finishAndUCCode:
                                item.finishAndUCCodeLabel ||
                                this.resolveFinishAndUCCode(item),
                            notes: item.notes || '',
                            container:
                                packingList.container ||
                                packingList.vehiclePlate ||
                                '',
                            shippedOn,
                            packingQty: item.packingQty ?? '',
                            warehouseReceipts: item.warehouseReceipts || [],
                            qtyWarehouseReceipt: this.getTotalWarehouseReceived(
                                item
                            ),
                            sentToSite: item.sentToSite || [],
                            qtySentToSite: this.getTotalSentToSite(item),
                            siteReceipts: item.siteReceipts || [],
                            qtySiteReceipt: this.getTotalSiteReceived(item),
                            projectId: packingList.projectId,
                            packingListId: packingList.id,
                            packageId: pkg.id,
                            workOrderId: item.workOrderId || null,
                            itemIndex,
                            isDeleted: packingList.isDeleted === true,
                        }
                        rows.push({
                            ...row,
                            rowKey: this.getRowKey(row),
                        })
                    })
                })
            })
            return rows
        },
        resolveFinishAndUCCode(item) {
            if (!item.finishAndUCCode || !this.settingFinish?.finishes) {
                return item.finishAndUCCode || ''
            }
            const finish = this.settingFinish.finishes.find(
                e => e.id == item.finishAndUCCode
            )
            return finish?.name || item.finishAndUCCode
        },
        getTotalWarehouseReceived(item) {
            const warehouseReceipts = item.warehouseReceipts || []
            if (warehouseReceipts.length > 0) {
                return warehouseReceipts.reduce(
                    (sum, receipt) => sum + Number(receipt.qty || 0),
                    0
                )
            }
            return Number(item.qtyWarehouseReceipt || 0)
        },
        isWarehouseReceiptComplete(item) {
            return (
                this.getTotalWarehouseReceived(item) >=
                Number(item.packingQty || 0)
            )
        },
        getTotalSentToSite(item) {
            const sentToSite = item.sentToSite || []
            if (sentToSite.length > 0) {
                return sentToSite.reduce(
                    (sum, entry) => sum + Number(entry.qty || 0),
                    0
                )
            }
            return Number(item.qtySentToSite || 0)
        },
        getTotalSiteReceived(item) {
            const siteReceipts = item.siteReceipts || []
            if (siteReceipts.length > 0) {
                return siteReceipts.reduce(
                    (sum, receipt) => sum + Number(receipt.qty || 0),
                    0
                )
            }
            return Number(item.qtySiteReceipt || 0)
        },
        getRemainingToSend(item) {
            const warehouseReceived = this.getTotalWarehouseReceived(item)
            if (warehouseReceived <= 0) {
                return 0
            }
            return Math.max(
                warehouseReceived - this.getTotalSentToSite(item),
                0
            )
        },
        getRowKey(row) {
            return `${row.packingListId}_${row.packageId}_${row.itemIndex}`
        },
        formatTimestamp(timestamp) {
            if (!timestamp) {
                return ''
            }
            const seconds = timestamp._seconds || timestamp.seconds
            if (seconds) {
                return moment.unix(seconds).format('DD/MM/YYYY')
            }
            return moment(timestamp).format('DD/MM/YYYY')
        },
        async resolveWorkOrderId(item) {
            if (item.workOrderId) {
                return item.workOrderId
            }
            if (!item.workOrder) {
                return null
            }
            if (this.workOrderIdByCode[item.workOrder]) {
                return this.workOrderIdByCode[item.workOrder]
            }
            const result = await API.getESMWorkOrderId(item.workOrder)
            const workOrderId = result?.id || null
            if (workOrderId) {
                this.$set(this.workOrderIdByCode, item.workOrder, workOrderId)
                this.items = this.items.map(row =>
                    row.workOrder === item.workOrder
                        ? { ...row, workOrderId }
                        : row
                )
            }
            return workOrderId
        },
        async openPartNumberPdf(item) {
            try {
                if (!item.itemCode) {
                    return
                }
                const workOrderId = await this.resolveWorkOrderId(item)
                if (!workOrderId) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: `Work order not found for item ${
                            item.itemCode
                        } (WO ${item.workOrder || '-'}).`,
                    })
                    return
                }
                this.loading = true
                const storagePath = `${this.sourceCompanyId}/workOrders/${workOrderId}/items`
                // Same extensions allowed on work order part numbers
                const extensions = ['pdf', 'dxf', 'stp']
                let url = ''
                for (const extension of extensions) {
                    url = await loadImage(
                        storagePath,
                        `${item.itemCode}.${extension}`
                    )
                    if (url) {
                        break
                    }
                }
                if (url) {
                    setTimeout(() => {
                        window.open(url, '_blank')
                        this.loading = false
                    })
                    return
                }
                this.setErrorItems({
                    source: this.$options.name,
                    message: `No file found for item ${item.itemCode} (pdf/dxf/stp).`,
                })
                this.loading = false
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                this.loading = false
            }
        },
        openWarehouseReceipt(item) {
            if (!this.canRecordWarehouseReceipt) {
                return
            }
            this.selectedItem = { ...item }
            this.openWarehouseDialog = true
        },
        openSiteReceipt(item) {
            if (!this.canRecordSiteReceipt) {
                return
            }
            this.selectedItem = { ...item }
            this.openSiteDialog = true
        },
        async openSentToSite(item) {
            if (!this.canSendToSite) {
                return
            }
            this.selectedItem = { ...item }
            try {
                await this.waitForFullLoad()
                this.loading = true
                const availableItems = this.items.filter(row => {
                    const remaining =
                        (Number(row.qtyWarehouseReceipt) || 0) -
                        (Number(row.qtySentToSite) || 0)
                    return remaining > 0
                })
                this.sentToSiteAvailableItems = availableItems.map(row => {
                    const remaining =
                        (Number(row.qtyWarehouseReceipt) || 0) -
                        (Number(row.qtySentToSite) || 0)
                    return {
                        ...row,
                        rowKey: this.getRowKey(row),
                        warehouseReceived: Number(row.qtyWarehouseReceipt) || 0,
                        alreadySent: Number(row.qtySentToSite) || 0,
                        remainingQty: remaining,
                    }
                })
                await this.ensureProjectsLoaded()
                this.openSentToSiteDialog = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        patchItemTracking(detailedRows) {
            const byKey = {}
            detailedRows.forEach(row => {
                byKey[row.rowKey] = row
            })
            this.items = this.items.map(row => {
                const detailed = byKey[row.rowKey]
                if (!detailed) {
                    return row
                }
                return {
                    ...row,
                    warehouseReceipts: detailed.warehouseReceipts,
                    sentToSite: detailed.sentToSite,
                    siteReceipts: detailed.siteReceipts,
                    qtyWarehouseReceipt: detailed.qtyWarehouseReceipt,
                    qtySentToSite: detailed.qtySentToSite,
                    qtySiteReceipt: detailed.qtySiteReceipt,
                }
            })
        },
        async ensureItemTracking(item) {
            if (Array.isArray(item.sentToSite)) {
                return item
            }
            const result = await API.getESMStockPackingLists({
                packingListIds: [item.packingListId],
                includeTrackingDetails: true,
            })
            const detailedRows = this.mapStockRows(result?.items)
            this.patchItemTracking(detailedRows)
            return (
                detailedRows.find(
                    row =>
                        row.packingListId === item.packingListId &&
                        row.packageId === item.packageId &&
                        row.itemIndex === item.itemIndex
                ) || { ...item }
            )
        },
        async openSentToSiteTickets(item) {
            this.selectedItem = await this.ensureItemTracking(item)
            await this.ensureProjectsLoaded()
            this.openTicketsDialog = true
        },
        closeTicketsDialog() {
            this.openTicketsDialog = false
            this.selectedItem = {}
        },
        applySignedNameToItem(item, nameFile, signedNameFile) {
            if (!item || !nameFile || !signedNameFile) {
                return item
            }
            if (!Array.isArray(item.sentToSite) || !item.sentToSite.length) {
                return item
            }
            let changed = false
            const sentToSite = item.sentToSite.map(entry => {
                if (
                    entry?.nameFile === nameFile &&
                    entry.signedNameFile !== signedNameFile
                ) {
                    changed = true
                    return { ...entry, signedNameFile }
                }
                return entry
            })
            return changed ? { ...item, sentToSite } : item
        },
        async onSignedTicketUploaded(payload = {}) {
            if (payload.packingLists?.length) {
                await this.applyPackingListUpdates(payload.packingLists)
            }
            this.items = this.items.map(row =>
                this.applySignedNameToItem(
                    row,
                    payload.nameFile,
                    payload.signedNameFile
                )
            )
            const selected = this.items.find(
                row =>
                    row.packingListId === this.selectedItem.packingListId &&
                    row.packageId === this.selectedItem.packageId &&
                    row.itemIndex === this.selectedItem.itemIndex
            )
            this.selectedItem = this.applySignedNameToItem(
                selected || this.selectedItem,
                payload.nameFile,
                payload.signedNameFile
            )
            this.activateAlert = true
            this.alertMessage = 'Signed delivery ticket uploaded successfully'
        },
        closeWarehouseDialog() {
            this.openWarehouseDialog = false
            this.selectedItem = {}
        },
        closeSiteDialog() {
            this.openSiteDialog = false
            this.selectedItem = {}
        },
        closeSentToSiteDialog() {
            this.openSentToSiteDialog = false
            this.selectedItem = {}
            this.sentToSiteAvailableItems = []
        },
        async onReceiptSaved(payload = {}) {
            if (payload.packingList) {
                this.applyPackingListUpdates(payload.packingList)
            }
            this.closeWarehouseDialog()
            this.closeSiteDialog()
            this.activateAlert = true
            this.alertMessage = 'Material receipt was successfully recorded'
        },
        async onSentToSiteSaved(payload = {}) {
            if (payload.packingLists?.length) {
                this.applyPackingListUpdates(payload.packingLists)
            }
            this.closeSentToSiteDialog()
            this.activateAlert = true
            this.alertMessage = 'Items were successfully marked as sent to site'
        },
    },
    async mounted() {
        if (!isStockESMViewerCompany(this.companyId)) {
            const fallbackRoute = this.getProjectStockFallbackRoute()
            this.$router.replace({
                name: fallbackRoute,
                params: {
                    ...this.$route.params,
                    projectId:
                        this.$route.params.projectId || this.$route.params.id,
                },
            })
            return
        }
        try {
            const {
                data: { user },
            } = await API.getMyInfo()
            this.loadStockESMPermissions(user)
            await this.loadItems()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
    position: sticky;
    z-index: 1;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
</style>
